"use client";

import React from "react";
import { HotkeysProvider } from "react-hotkeys-hook";
import { HotkeyHelpDialog } from "@/components/hotkey-help-dialog";
import { useHotkey } from "@/lib/hooks/use-hotkey";
import { useUIStore } from "@/lib/stores/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/ui/dialog";
import { SettingsContent } from "@/components/settings-content";
import { FeedbackDialog } from "@/components/feedback/feedback-dialog";
import { DebugPanel } from "@/ui/debug-panel";
import { useFeedbackEnabled } from "@/components/providers/feedback-config-provider";

function HotkeyHelp() {
  const hotkeyHelpOpen = useUIStore((s) => s.hotkeyHelpOpen);
  const setHotkeyHelpOpen = useUIStore((s) => s.setHotkeyHelpOpen);

  useHotkey("shift+slash", () => setHotkeyHelpOpen(true), {
    description: "Show keyboard shortcuts",
    preventDefault: true,
  });

  return <HotkeyHelpDialog open={hotkeyHelpOpen} onOpenChange={setHotkeyHelpOpen} />;
}

function SettingsDialog() {
  const settingsOpen = useUIStore((s) => s.settingsOpen);
  const setSettingsOpen = useUIStore((s) => s.setSettingsOpen);

  useHotkey("mod+comma", () => setSettingsOpen(true), {
    description: "Open settings",
    preventDefault: true,
  });

  return (
    <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Settings</DialogTitle>
        </DialogHeader>
        <SettingsContent />
      </DialogContent>
    </Dialog>
  );
}

function FeedbackMounted() {
  const feedbackOpen = useUIStore((s) => s.feedbackOpen);
  const setFeedbackOpen = useUIStore((s) => s.setFeedbackOpen);

  useHotkey("mod+shift+f", () => setFeedbackOpen(true), {
    description: "Give feedback",
    preventDefault: true,
    enableOnFormTags: true,
  });

  return <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />;
}

function Feedback() {
  return useFeedbackEnabled() ? <FeedbackMounted /> : null;
}

export function HotkeyProvider({ children }: { children: React.ReactNode }) {
  return (
    <HotkeysProvider>
      {children}
      <HotkeyHelp />
      <SettingsDialog />
      <Feedback />
      <DebugPanel />
    </HotkeysProvider>
  );
}
